import React, { Component } from "react";
import CancelBoardService from "../../services/cancelboardservice";

// Audit -> Cancel Reasons. Why customers cancel their OWN pickups, including the words they type.
// Data comes from lk_cancel_board_fact, rebuilt nightly, so the board shows how stale it is.
// CSS is prefixed .cb- so it cannot clobber the rest of the portal.
const CSS = `
.cb-wrap{padding:18px 22px;font-family:inherit;color:#313030}
.cb-h{display:flex;align-items:baseline;gap:14px;flex-wrap:wrap;margin-bottom:4px}
.cb-h1{font-size:20px;font-weight:600;margin:0}
.cb-stale{font-size:12px;color:#8a8a8a}
.cb-sub{font-size:13px;color:#6b6b6b;margin:0 0 16px}
.cb-bar{display:flex;gap:18px;flex-wrap:wrap;align-items:center;margin-bottom:18px}
.cb-seg{display:inline-flex;border:1px solid #e0dede;border-radius:8px;overflow:hidden}
.cb-seg button{border:0;background:#fff;padding:7px 14px;font-size:13px;cursor:pointer;color:#555}
.cb-seg button+button{border-left:1px solid #e0dede}
.cb-seg button.on{background:#ED0170;color:#fff}
.cb-lbl{font-size:12px;color:#8a8a8a;margin-right:-10px}
.cb-cards{display:flex;gap:14px;flex-wrap:wrap;margin-bottom:22px}
.cb-card{flex:1 1 170px;border:1px solid #eceaea;border-radius:12px;padding:14px 16px;background:#fff}
.cb-card .v{font-size:26px;font-weight:600;line-height:1.1}
.cb-card .k{font-size:12px;color:#8a8a8a;margin-top:4px}
.cb-card.bad .v{color:#c0392b}
.cb-card.good .v{color:#1e8e5a}
.cb-grid{display:flex;gap:22px;flex-wrap:wrap;align-items:flex-start}
.cb-panel{flex:1 1 360px;min-width:320px}
.cb-panel h3{font-size:14px;font-weight:600;margin:0 0 10px;color:#444}
.cb-tbl{width:100%;border-collapse:collapse;font-size:13px}
.cb-tbl th{text-align:left;font-weight:600;color:#8a8a8a;font-size:11.5px;text-transform:uppercase;letter-spacing:.4px;padding:0 8px 7px 0;border-bottom:1px solid #eceaea}
.cb-tbl td{padding:7px 8px 7px 0;border-bottom:1px solid #f5f4f4;vertical-align:top}
.cb-tbl td.n{text-align:right;width:52px;font-variant-numeric:tabular-nums}
.cb-tbl td.p{text-align:right;width:62px;color:#8a8a8a;font-variant-numeric:tabular-nums}
.cb-pill{display:inline-block;font-size:10.5px;padding:1px 6px;border-radius:20px;background:#fdf0f6;color:#ED0170;margin-left:6px}
.cb-quotes{margin:0;padding:0;list-style:none}
.cb-quote{border-left:3px solid #ED0170;background:#fcfbfb;padding:9px 12px;margin-bottom:9px;border-radius:0 8px 8px 0}
.cb-quote .t{font-size:13.5px;line-height:1.45}
.cb-quote .m{font-size:11px;color:#9a9a9a;margin-top:5px}
.cb-empty{font-size:13px;color:#9a9a9a;padding:10px 0}
.cb-err{font-size:13px;color:#c0392b;padding:10px 0}
.cb-warn{font-size:12.5px;line-height:1.5;background:#fff8e6;border:1px solid #f3e2b3;color:#7a5c12;border-radius:10px;padding:10px 13px;margin-bottom:18px}
.cb-range{font-size:12px;color:#8a8a8a}
.cb-note{font-size:11.5px;color:#9a9a9a;margin-top:6px;line-height:1.5}
`;

const WINDOWS = [7, 30, 90];
const CHANNELS = [
  { k: "", t: "All" },
  { k: "app", t: "App" },
  { k: "website", t: "Website" },
  { k: "whatsapp", t: "WhatsApp" },
  { k: "store", t: "Store" },
];

export default class CancelBoard extends Component {
  constructor(props) {
    super(props);
    this.service = new CancelBoardService();
    this.state = { days: 7, channel: "", board: null, loading: true, error: "" };
  }

  componentDidMount() { this.load(); }

  load = async () => {
    this.setState({ loading: true, error: "" });
    const board = await this.service.getBoard(this.state.days, this.state.channel);
    if (!board || board.error || board.statusCode === 401) {
      this.setState({ loading: false, error: (board && (board.error || board.message)) || "Could not load the board." });
      return;
    }
    this.setState({ board, loading: false });
  };

  pick = (patch) => this.setState(patch, this.load);

  // RebookHours is whole hours, so anything under an hour stores as 0 -- say so rather than
  // printing "0 hours", which reads like a bug.
  gap = (h) => {
    if (h === null || h === undefined) return "-";
    if (h < 1) return "within the hour";
    if (h < 24) return h + "h";
    if (h < 168) return Math.round(h / 24) + "d";
    return Math.round(h / 168) + "w";
  };

  render() {
    const { board, loading, error, days, channel } = this.state;
    const s = (board && board.summary) || {};
    const meta = (board && board.meta) || {};
    const reasons = (board && board.reasons) || [];
    const cohorts = (board && board.cohorts) || [];
    const quotes = (board && board.freeText) || [];
    const lead = (board && board.leadTimes) || [];
    const chans = (board && board.channels) || [];
    const rb = (board && board.rebook) || {};
    const buckets = (board && board.rebookBuckets) || [];
    const byReason = (board && board.rebookByReason) || [];
    const forCohort = (name) => cohorts.filter((c) => c.cohort === name);

    return (
      <div className="cb-wrap">
        <style>{CSS}</style>

        <div className="cb-h">
          <h2 className="cb-h1">Cancel Reasons</h2>
          <span className="cb-range">
            {meta.firstSeen ? meta.firstSeen + "  to  " + meta.lastSeen : (meta.from ? meta.from + " to " + meta.to : "")}
          </span>
          <span className="cb-stale">
            {meta.refreshedOn ? "rebuilt " + meta.refreshedOn : "not yet refreshed"}
          </span>
        </div>
        <p className="cb-sub">
          Why customers cancelled their own pickups. Staff-recorded cancellations are excluded.
          Rebuilt nightly.
        </p>

        <div className="cb-bar">
          <span className="cb-lbl">Window</span>
          <div className="cb-seg">
            {WINDOWS.map((d) => (
              <button key={d} className={d === days ? "on" : ""} onClick={() => this.pick({ days: d })}>
                {d}d
              </button>
            ))}
          </div>
          <span className="cb-lbl">Channel</span>
          <div className="cb-seg">
            {CHANNELS.map((c) => (
              <button key={c.k || "all"} className={c.k === channel ? "on" : ""} onClick={() => this.pick({ channel: c.k })}>
                {c.t}
              </button>
            ))}
          </div>
        </div>

        {loading && <div className="cb-empty">Loading...</div>}
        {!loading && error && <div className="cb-err">{error}</div>}

        {!loading && !error && (
          <div>
            {meta.straddlesChange === 1 && (
              <div className="cb-warn">
                <b>This window spans the {meta.optionsChangedOn} change.</b> The reason options were
                replaced that day, so older cancellations here carry the previous labels
                ("I change my mind", "Others", "No Show - Pickup Executive") and newer ones carry
                the current set. The percentages below are a blend of two different option lists --
                for a clean read of the new options, wait until 7d sits entirely after
                {" " + meta.optionsChangedOn + "."}
              </div>
            )}

            <div className="cb-cards">
              <div className="cb-card">
                <div className="v">{s.total || 0}</div>
                <div className="k">customer cancellations</div>
              </div>
              <div className="cb-card good">
                <div className="v">{s.informative || 0}</div>
                <div className="k">gave a real reason</div>
              </div>
              <div className="cb-card bad">
                <div className="v">{(s.pctNoInfo || 0) + "%"}</div>
                <div className="k">told us nothing (was 76%)</div>
              </div>
              <div className="cb-card">
                <div className="v">{s.freeText || 0}</div>
                <div className="k">typed their own words</div>
              </div>
            </div>

            <div className="cb-grid">
              <div className="cb-panel">
                <h3>Reasons</h3>
                {reasons.length === 0 && <div className="cb-empty">No cancellations in this window.</div>}
                {reasons.length > 0 && (
                  <table className="cb-tbl">
                    <thead>
                      <tr><th>Reason</th><th className="n">n</th><th className="p">%</th></tr>
                    </thead>
                    <tbody>
                      {reasons.map((r, i) => (
                        <tr key={i}>
                          <td>
                            {r.reason}
                            {r.isFreeText ? <span className="cb-pill">typed</span> : null}
                          </td>
                          <td className="n">{r.n}</td>
                          <td className="p">{r.pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <h3 style={{ marginTop: 22 }}>Lead time</h3>
                <table className="cb-tbl">
                  <tbody>
                    {lead.map((l, i) => (
                      <tr key={i}><td>{l.leadTime}</td><td className="n">{l.n}</td></tr>
                    ))}
                  </tbody>
                </table>

                <h3 style={{ marginTop: 22 }}>Channel</h3>
                <table className="cb-tbl">
                  <thead>
                    <tr><th>Channel</th><th className="n">n</th><th className="p">no info</th></tr>
                  </thead>
                  <tbody>
                    {chans.map((c, i) => (
                      <tr key={i}><td>{c.channel}</td><td className="n">{c.n}</td><td className="p">{c.noInfo}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="cb-panel">
                <h3>New customers vs established</h3>
                <div className="cb-grid">
                  {["brand new", "established"].map((coh) => (
                    <div className="cb-panel" key={coh} style={{ minWidth: 150 }}>
                      <table className="cb-tbl">
                        <thead>
                          <tr><th>{coh}</th><th className="n">n</th></tr>
                        </thead>
                        <tbody>
                          {forCohort(coh).length === 0 && (
                            <tr><td colSpan="2" className="cb-empty">none</td></tr>
                          )}
                          {forCohort(coh).map((c, i) => (
                            <tr key={i}><td>{c.reason}</td><td className="n">{c.n}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>

                <h3 style={{ marginTop: 22 }}>In their own words</h3>
                {quotes.length === 0 && (
                  <div className="cb-empty">
                    Nobody typed a reason in this window. Widen it to 90d to see earlier ones.
                  </div>
                )}
                <ul className="cb-quotes">
                  {quotes.map((q, i) => (
                    <li className="cb-quote" key={i}>
                      <div className="t">{q.text}</div>
                      <div className="m">
                        {q.channel} &middot; {q.cohort} &middot; {q.cancelledAt} &middot; pickup #{q.pickupId}
                        {q.rebookHours === null || q.rebookHours === undefined
                          ? " \u00b7 never rebooked"
                          : " \u00b7 rebooked " + this.gap(q.rebookHours)}
                      </div>
                    </li>
                  ))}
                </ul>

                <h3 style={{ marginTop: 22 }}>Did they come back?</h3>
                <div className="cb-cards">
                  <div className="cb-card">
                    <div className="v">{(rb.pct || 0) + "%"}</div>
                    <div className="k">booked again</div>
                  </div>
                  <div className="cb-card">
                    <div className="v">{rb.medianHours === 0 ? "<1h" : this.gap(rb.medianHours)}</div>
                    <div className="k">typical gap</div>
                  </div>
                </div>
                <table className="cb-tbl">
                  <thead>
                    <tr><th>Rebooked after</th><th className="n">n</th></tr>
                  </thead>
                  <tbody>
                    {buckets.map((b, i) => (
                      <tr key={i}><td>{b.bucket}</td><td className="n">{b.n}</td></tr>
                    ))}
                    {buckets.length === 0 && <tr><td colSpan="2" className="cb-empty">nobody rebooked</td></tr>}
                  </tbody>
                </table>
                <div className="cb-note">
                  Of {rb.eligible || 0} cancellations old enough to judge, {rb.rebooked || 0} booked
                  again. {rb.tooRecent || 0} were cancelled in the last 48 hours and are left out of
                  the rate, since they have barely had the chance.
                </div>

                <h3 style={{ marginTop: 22 }}>Which reasons they come back from</h3>
                <table className="cb-tbl">
                  <thead>
                    <tr><th>Reason</th><th className="n">back</th><th className="p">of</th><th className="p">%</th></tr>
                  </thead>
                  <tbody>
                    {byReason.map((r, i) => (
                      <tr key={i}>
                        <td>{r.reason}</td>
                        <td className="n">{r.rebooked}</td>
                        <td className="p">{r.total}</td>
                        <td className="p">{r.pct}%</td>
                      </tr>
                    ))}
                    {byReason.length === 0 && <tr><td colSpan="4" className="cb-empty">not enough data yet</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}
